define("Pages/GroupSales", [`Page`, `module`], function (Super, Self) {
	const DEFAULT_THEME = `DARK`;

	return Super.inherit({
		namespace: Self.id,
		requires: [`Groups`, `XHR`],
		async constructor(setup) {
			const { App, Config, Groups, System, container, data, instances } = this;
			const { PRIMARY_DOMAIN } = Config;
			const { trackingOptions } = data;
			const { isMobile } = System.get();
			const promises = [];
			const queryParams = App.getQueryParams();
			const groupId = setup.groupId != null ? setup.groupId : queryParams.groupId;
			const groupSaleId = setup.groupSaleId != null ? setup.groupSaleId : queryParams.gsId;

			const groupSale = await Groups.getGroupSale({ groupId, id: groupSaleId });
			const groupSaleEvents = await Groups.getGroupSaleEvents({ groupId, id: groupSaleId });
			const { group } = groupSale;

			const { subdomain } = groupSale;
			const theme = this.isString(groupSale.theme, 1) ? groupSale.theme : DEFAULT_THEME;
			const groupUrl = `${PRIMARY_DOMAIN}/groups/${subdomain}`;
			const title = `${groupSale.name} by ${group.name} | Tixr`;
			const shareUrl = window.location.href;
			const accessCodeParams = queryParams.ac;
			const { backgroundUrl, backgroundUrlv2, hiddenDetails, mobileBackgroundImageUrl, mobileBackgroundImageUrlv2 } =
				groupSale;
			const backupBackgroundUrl = `${Config.STATIC_IMG_PATH}/group-bg.jpg`;
			let clientSetBackgroundUrl = backgroundUrlv2;
			let clientSetMobileBackgroundUrl = mobileBackgroundImageUrlv2;

			if (!this.isString(clientSetBackgroundUrl, 1) && this.isString(backgroundUrl, 1)) {
				clientSetBackgroundUrl = backgroundUrl;
				this.addClass(`legacy-bg-desktop`);
			}

			if (!this.isString(clientSetMobileBackgroundUrl, 1) && this.isString(mobileBackgroundImageUrl, 1)) {
				clientSetMobileBackgroundUrl = mobileBackgroundImageUrl;
				this.addClass(`legacy-bg-mobile`);
			}

			trackingOptions.pageType = `Group Sale`;
			trackingOptions.groupSaleId = groupSale.id;
			data.uid = `gs.${groupSale.id}`;

			container.setAttribute(`theme`, theme);

			this.assign(data, {
				accessCode: this.isString(accessCodeParams, 1) ? accessCodeParams : null,
				backgroundUrl: this.isString(clientSetBackgroundUrl, 1) ? clientSetBackgroundUrl : backupBackgroundUrl,
				groupSaleEvents,
				hasContact: this.isString(groupSale.contactEmail, 1) || this.isString(groupSale.contactPhone, 1),
				mobileBackgroundUrl: this.isString(clientSetMobileBackgroundUrl, 1)
					? clientSetMobileBackgroundUrl
					: backupBackgroundUrl,
				PRIMARY_DOMAIN,
				groupSale,
				theme,
				groupId,
				groupSaleId,
				groupUrl,
				title,
				unlocked: false,
			});

			const showSiteLinks = !this.includes(hiddenDetails, `FOOTER`);
			let promise;

			if (isMobile && this.isString(groupSale.name, 60)) {
				groupSale.showTooltip = true;
			}

			this.render();

			const shareLinkOverlaySetup = {
				type: `custom`,
				url: shareUrl,
				theme: `DARK`,
				overlayMode: `modal`,
				style: `ux4`,
				size: `small`,
				customHeader: ` Share with your friends`,
				customImage: groupSale.logoUrl,
				customMessage: `${groupSale.name} - get your tickets here!`,
				emailSubject: `${groupSale.name} - get your tickets here!`,
				emailBody: `You can get your tickets here!\n\n${shareUrl}`,
			};

			promises.push(
				this.create({
					namespace: `Modules/PageHeader`,
					setup: {
						name: `page-header`,
						desktopBackgroundWhenScrolled: false,
						hideOnMobileScroll: true,
						showTixrLogo: false,
						showUserLinks: false,
						leftLinks: [
							{
								type: `image`,
								alt: group.name,
								href: group.externalUrl,
								src: group.logoUrl,
								desktop: true,
								app: false,
								mobile: false,
							},
							{
								type: `icon`,
								mobile: true,
								desktop: false,
								app: false,
								href: `/`,
								icon: `arrow-left`,
							},
						],
						rightLinks: [
							{
								label: `Share`,
								handler: (evt) =>
									App.loadOverlay({
										type: `Share`,
										setup: shareLinkOverlaySetup,
									}),
								theme: `BLACK`,
								type: `button`,
								icon: `share-up`,
								mobile: false,
							},
							{
								handler: (evt) =>
									App.loadOverlay({
										type: `Share`,
										setup: shareLinkOverlaySetup,
									}),
								theme: `BLACK`,
								type: `icon`,
								icon: `share-up`,
								desktop: false,
							},
						],
						callbacks: {
							onHideStateChange: ({ hidden }) => {
								if (hidden) {
									this.addClass(`header-hidden`, container);
								} else {
									this.removeClass(`header-hidden`, container);
								}
							},
						},
					},
				}),
				this.create({
					namespace: `Modules/PageFooter`,
					setup: {
						name: `page-footer`,
						showSiteLinks,
					},
				}),
				this.create({
					namespace: `Modules/ParallaxBlur`,
					setup: {
						name: `parallax-blur-desktop`,
						backgroundImageUrl: data.backgroundUrl,
						scrollContainer: this.container,
					},
				}),
				this.create({
					namespace: `Modules/ParallaxBlur`,
					setup: {
						name: `parallax-blur-mobile`,
						backgroundImageUrl: data.mobileBackgroundUrl,
						scrollContainer: this.container,
					},
				})
			);

			if (groupSale.hacl && !this.isString(accessCodeParams, 1)) {
				promises.push(
					App.loadOverlay({
						type: `EnterAccessCode`,
						setup: {
							accessCodeUnlockMode: `groupSale`,
							callbacks: {
								onSubmit: async ({ accessCode }) => {
									const response = await Groups.applyGroupSaleAccessCode({
										groupId,
										id: groupSaleId,
										accessCode,
									});
									if (response.valid) {
										data.unlocked = true;
										data.accessCode = accessCode;
										instances.enterAccessCodeOverlay.destroy();
									}
								},
								die: () => {
									if (data.unlocked) {
										this.populateEventList();
									} else {
										App.loadPath({ href: `/` });
									}
								},
							},
						},
						then: (overlay) => {
							instances.enterAccessCodeOverlay = overlay;
						},
					})
				);
			} else {
				promises.push(this.populateEventList());
			}

			this.setBackgroundContrast();
			this.setReadMoreVisibility();

			promises.push(promise);

			return this.when({ promises });
		},

		async ready() {
			await this.defer({ duration: 100 });
			this.App.setQueryParam(`ac`, null, true);
			this.App.setQueryParam(`groupId`, null, true);
			this.App.setQueryParam(`gsId`, null, true);
		},

		api: {
			getCarouselList: {
				value({ carousels }) {
					const { data, App, Templates } = this;
					const { accessCode, groupId, groupSaleId } = data;
					const newCarousels = carousels;
					const printNumber = Templates.getHelper(`printNumber`);
					const gsurl = App.getPath();

					this.each(newCarousels, (carousel) => {
						carousel.moreInfoHidden = true;

						this.each(carousel.items, (item) => {
							const { gst, id } = item;
							const params = { gst: gst, gsurl, A: `L`, groupId, gsId: groupSaleId };

							if (this.isString(accessCode, 1)) {
								params.ac = accessCode;
							}

							item.externalUrl = `/e/${id}?${App.serializeParams(params)}`;

							if (this.isFinite(item.minPrice)) {
								let text = printNumber(item.minPrice, `currency`, item.currency);
								if (item.hasMultiplePrices) {
									text += `+`;
								}

								item.badges = {
									topRight: [
										{
											text,
											color: `blue`,
										},
									],
								};
							}
						});
					});

					return newCarousels;
				},
			},

			setReadMoreVisibility: {
				value() {
					const { ResizeObserver } = window;
					const textElement = this.get(`[name="description"] > .text`);
					const readMoreButton = this.get(`[action="read-more"]`);
					const observer = new ResizeObserver((entries) => {
						const threshold = 1;
						for (let entry of entries) {
							if (entry.target.scrollHeight > entry.contentRect.height + threshold) {
								this.removeClass(`hidden`, readMoreButton);
							} else {
								this.addClass(`hidden`, readMoreButton);
							}
						}
					});

					if (this.isObject(textElement)) {
						observer.observe(textElement);
					}
				},
			},

			setBackgroundContrast: {
				value() {
					const { data } = this;
					const { groupSale } = data;
					let customOpacity = 0;

					if (this.isFinite(groupSale.backgroundContrastPercentage, 0)) {
						customOpacity = groupSale.backgroundContrastPercentage * 0.01;
					}

					this.get(`[name="background-contrast"]`).style.opacity = customOpacity;
				},
			},

			populateEventList: {
				value() {
					const { data } = this;
					const { groupSaleEvents } = data;
					const hasCarousels = this.isArray(groupSaleEvents.carousels, 1);
					const noEventsElement = this.get(`.no-events`);

					if (hasCarousels) {
						this.hide({ target: noEventsElement });
						return this.create({
							namespace: `Modules/CarouselList`,
							setup: {
								carousels: this.getCarouselList({ carousels: groupSaleEvents.carousels }),
								desktopMergeIntoHeader: true,
								mode: `ux3`,
								name: `event-list`,
								showAnchorLinks: true,
							},
						});
					} else {
						this.show({ target: noEventsElement });
					}
				},
			},
		},

		listeners: [
			{
				event: `click`,
				filter: `[action="read-more"]`,
				handler(evt) {
					const { data, App } = this;
					const { groupSale } = data;

					App.loadOverlay({
						type: `Message`,
						setup: {
							type: `info`,
							heading: groupSale.name, // Keeping for now in case release is before other ux4 modal changes
							message: groupSale.description,
							overlayMode: `modal`,
							style: `ux4`,
							size: `large`,
						},
					});
				},
			},
		],
	});
});

define("Pages/GroupSales.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Pages/GroupSales/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"content transition-content\"> <div class=\"hero-wrapper\"> <div class=\"details-wrapper\"> <div class=\"details\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"logoUrl") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":621},"end":{"line":1,"column":696}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"headline") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":697},"end":{"line":1,"column":758}}})) != null ? stack1 : "")
    + " <h1 class=\"name\""
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"showTooltip") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":775},"end":{"line":1,"column":830}}})) != null ? stack1 : "")
    + ">"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</h1> <div name=\"description\"> <p class=\"text\">"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"description") : depth0), depth0))
    + "</p> <a href=\"#\" action=\"read-more\" class=\"button ux4\">Read More</a> </div> </div> </div> </div> <h2 class=\"no-events transition-content\">There are no upcoming events</h2> <div class=\"events transition-content\"> <div name=\"event-list\"></div> </div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"hasContact")),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1150},"end":{"line":1,"column":1836}}})) != null ? stack1 : "")
    + " </div> ";
},"2":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<img class=\"logo\" src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"logoUrl") : depth0), depth0))
    + "\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" />";
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<h2 class=\"headline\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"headline") : depth0), depth0))
    + "</h2>";
},"6":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " data-tooltip-text=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\"";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"contact transition-content ux4-container\"> <div> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"logoUrl") : depth0),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1236},"end":{"line":1,"column":1333}}})) != null ? stack1 : "")
    + " <div class=\"contact-info\"> <h2>Contact "
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "</h2> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"contactName") : depth0),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1387},"end":{"line":1,"column":1458}}})) != null ? stack1 : "")
    + " </div> <div class=\"contact-actions\"> "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"contactPhone") : depth0),{"name":"if","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1496},"end":{"line":1,"column":1649}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"contactEmail") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1650},"end":{"line":1,"column":1807}}})) != null ? stack1 : "")
    + " </div> </div> </div> ";
},"9":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <img class=\"contact-logo hide-mobile\" src=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"logoUrl") : depth0), depth0))
    + "\" alt=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"name") : depth0), depth0))
    + "\" /> ";
},"11":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <p class=\"contact-name\">"
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"contactName") : depth0), depth0))
    + "</p> ";
},"13":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"tel:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"contactPhone") : depth0), depth0))
    + "\" class=\"button ux4 tertiary medium\" theme=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\">"
    + alias2(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"profile",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1600},"end":{"line":1,"column":1621}}}))
    + " "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"contactPhone") : depth0), depth0))
    + "</a>";
},"15":function(container,depth0,helpers,partials,data) {
    var alias1=container.lambda, alias2=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<a href=\"mailto:"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"contactEmail") : depth0), depth0))
    + "\" class=\"button ux4 tertiary medium\" theme=\""
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\">"
    + alias2(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"envelope",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1757},"end":{"line":1,"column":1779}}}))
    + " "
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"contactEmail") : depth0), depth0))
    + "</a>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"head-fixed hide-mobile\">  <h1>Tixr - "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"group") : depth0)) != null ? lookupProperty(stack1,"name") : stack1), depth0))
    + "</h1> </div>  <header class=\"head-fixed\"> <div name=\"page-header\"></div> </header> <div name=\"parallax-blur-desktop\" class=\"hide-mobile\"></div> <div name=\"parallax-blur-mobile\" class=\"hide-desktop\"></div> <div name=\"background-contrast\" class=\"hide-mobile\"></div> <div name=\"background-blur\"></div> <div name=\"background-gradient\"></div> <main class=\"body\"> "
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"groupSale") : depth0),{"name":"with","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":481},"end":{"line":1,"column":1853}}})) != null ? stack1 : "")
    + " </main> <footer class=\"content footer\"> <div name=\"page-footer\"></div> </footer>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=GroupSales.js.map